import { useState, useEffect } from 'react';
import { Plus, Shield, Trash2 } from 'lucide-react';
import { Card, Table, Badge, Button, ConfirmModal, Input } from '@/components/common';
import { policyService } from '@/services/policyService';
import type { AccessPolicy } from '@/types';

export function PoliciesPage() {
  const [policies, setPolicies] = useState<AccessPolicy[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newPolicy, setNewPolicy] = useState({
    name: '',
    description: '',
    resource: '',
    action: '',
    effect: 'allow' as 'allow' | 'deny',
    priority: 1,
  });

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await policyService.getPolicies({ per_page: 50 });
      setPolicies(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load policies');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePolicy = async () => {
    setLoading(true);
    setError('');
    try {
      await policyService.createPolicy(newPolicy);
      setCreateModalOpen(false);
      setNewPolicy({
        name: '',
        description: '',
        resource: '',
        action: '',
        effect: 'allow',
        priority: 1,
      });
      fetchPolicies();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create policy');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePolicy = async () => {
    if (!policyToDelete) return;
    setLoading(true);
    try {
      await policyService.deletePolicy(policyToDelete);
      setDeleteModalOpen(false);
      setPolicyToDelete(null);
      fetchPolicies();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete policy');
    } finally {
      setLoading(false);
    }
  };

  const filteredPolicies = policies.filter(policy =>
    policy.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    policy.resource.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const columns = [
    {
      key: 'name',
      title: 'Policy',
      render: (_: any, policy: AccessPolicy) => (
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-indigo-600" />
          <div>
            <p className="font-medium text-secondary-900">{policy.name}</p>
            <p className="text-xs text-secondary-500">{policy.description}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'resource',
      title: 'Resource',
      render: (_: any, policy: AccessPolicy) => (
        <span className="text-sm text-secondary-600">{policy.resource}</span>
      ),
    },
    {
      key: 'action',
      title: 'Action',
      render: (_: any, policy: AccessPolicy) => (
        <Badge variant="info">{policy.action}</Badge>
      ),
    },
    {
      key: 'effect',
      title: 'Effect',
      render: (_: any, policy: AccessPolicy) => (
        <Badge variant={policy.effect === 'allow' ? 'success' : 'danger'}>
          {policy.effect}
        </Badge>
      ),
    },
    {
      key: 'priority',
      title: 'Priority',
      render: (_: any, policy: AccessPolicy) => (
        <span className="text-sm text-secondary-600">{policy.priority}</span>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      render: (_: any, policy: AccessPolicy) => (
        <Badge variant={policy.is_active ? 'success' : 'warning'}>
          {policy.is_active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      title: '',
      width: '100px',
      render: (_: any, policy: AccessPolicy) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setPolicyToDelete(policy.id);
            setDeleteModalOpen(true);
          }}
          className="p-1.5 hover:bg-red-100 rounded transition-colors"
          title="Delete"
        >
          <Trash2 className="w-4 h-4 text-red-600" />
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">Access Policies</h1>
          <p className="text-secondary-600 mt-1">Manage resource access control policies</p>
        </div>
        <Button icon={<Plus className="w-4 h-4" />} onClick={() => setCreateModalOpen(true)}>
          New Policy
        </Button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>
      )}

      <Input
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder="Search policies..."
        className="max-w-md"
      />

      <Card padding="none">
        <Table
          data={filteredPolicies}
          columns={columns}
          loading={loading}
          emptyMessage="No policies found"
        />
      </Card>

      {createModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h2 className="text-2xl font-bold mb-4">Create Access Policy</h2>
            <div className="space-y-4">
              <Input
                label="Policy Name"
                value={newPolicy.name}
                onChange={(e) => setNewPolicy({ ...newPolicy, name: e.target.value })}
                placeholder="Allow API Access"
                required
              />
              <Input
                label="Description"
                value={newPolicy.description}
                onChange={(e) => setNewPolicy({ ...newPolicy, description: e.target.value })}
                placeholder="Policy description"
              />
              <Input
                label="Resource"
                value={newPolicy.resource}
                onChange={(e) => setNewPolicy({ ...newPolicy, resource: e.target.value })}
                placeholder="api/*"
                required
              />
              <Input
                label="Action"
                value={newPolicy.action}
                onChange={(e) => setNewPolicy({ ...newPolicy, action: e.target.value })}
                placeholder="read, write, delete"
                required
              />
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Effect</label>
                <select
                  value={newPolicy.effect}
                  onChange={(e) => setNewPolicy({ ...newPolicy, effect: e.target.value as 'allow' | 'deny' })}
                  className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="allow">Allow</option>
                  <option value="deny">Deny</option>
                </select>
              </div>
              <Input
                label="Priority"
                type="number"
                value={newPolicy.priority.toString()}
                onChange={(e) => setNewPolicy({ ...newPolicy, priority: parseInt(e.target.value) || 1 })}
                placeholder="1"
              />
            </div>
            <div className="flex gap-3 mt-6">
              <Button onClick={handleCreatePolicy} disabled={loading || !newPolicy.name}>
                Create Policy
              </Button>
              <Button variant="outline" onClick={() => setCreateModalOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setPolicyToDelete(null);
        }}
        onConfirm={handleDeletePolicy}
        title="Delete Policy"
        description="Are you sure you want to delete this policy? This may affect resource access."
        confirmText="Delete"
        variant="danger"
        loading={loading}
      />
    </div>
  );
}
